import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogIn } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const { signIn, isAuthenticated, authMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/saved';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Enter your email and password.'); return; }
    setSubmitting(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back.');
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.message || 'Sign in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const ic = 'giwahs-auth-input mt-2';

  return (
    <div data-testid="login-page" className="surface-obsidian">
      <SEO
        title="Sign in — GIWAHS Buyer Account"
        description="Sign in to your GIWAHS buyer account to access your saved factories, compare lists, and RFQ history."
        noindex
      />
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-gold">/ Buyer Account</span>
            <h1 className="mt-4 font-display tracking-tighter text-platinum" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              Sign in.
            </h1>
            <p className="mt-5 max-w-md text-platinum-2">
              Access your saved factories, your compare lists, and any RFQs you've sent.
            </p>
            <div className="mt-8 border-t border-hairline pt-5 text-sm text-muted-warm">
              No account yet? <Link to="/signup" data-testid="login-to-signup-link" className="underline underline-offset-4 text-platinum-2 hover:text-gold transition-colors">Create one — it takes 20 seconds</Link>.
            </div>
            {authMode === 'mock' && (
              <div data-testid="login-mock-mode-notice" className="giwahs-auth-notice mt-6 p-3 text-xs">
                <span className="label-overline text-gold">Dev mode</span>
                <p className="mt-1 text-platinum-2">Auth is running in local mock mode — accounts persist to your browser only. Set <code className="font-mono-data text-gold">REACT_APP_SUPABASE_URL</code> and <code className="font-mono-data text-gold">REACT_APP_SUPABASE_ANON_KEY</code> to enable real Supabase Auth.</p>
              </div>
            )}
          </div>

          <div className="md:col-span-7">
            <form data-testid="login-form" onSubmit={submit} className="giwahs-auth-panel space-y-5 p-6 md:p-10">
              <div>
                <label className="label-overline text-gold">Email</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email"
                  className={ic}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="label-overline text-gold">Password</label>
                  <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-muted-warm underline underline-offset-4 hover:text-gold transition-colors">Forgot?</Link>
                </div>
                <input
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  className={ic}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error && (
                <div data-testid="login-error" role="alert" aria-live="polite" className="giwahs-auth-error px-3 py-2 text-sm">
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={submitting}
                className="btn-giwahs btn-giwahs-primary w-full justify-center"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : <><LogIn size={14} strokeWidth={1.5} /> Sign in</>}
              </button>

              <p className="text-xs text-muted-warm">
                By signing in you agree to our <Link to="/terms" className="underline hover:text-gold transition-colors">Terms</Link> and <Link to="/privacy" className="underline hover:text-gold transition-colors">Privacy Policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
